import { useState, useEffect } from 'react';
import CourseDatePicker from '../course/CourseDatePicker';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

const WESTERN_MIN = 2026;
const WESTERN_MAX = 2050;
const MINGUO_MIN = WESTERN_MIN - 1911;
const MINGUO_MAX = WESTERN_MAX - 1911;

function parseYear(input) {
  const n = parseInt(String(input).trim(), 10);
  if (isNaN(n)) return null;
  if (n >= WESTERN_MIN && n <= WESTERN_MAX) return n;
  if (n >= MINGUO_MIN && n <= MINGUO_MAX) return n + 1911;
  return null;
}

async function fetchSemesters(institutionId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/semesters?select=*&institution_id=eq.${institutionId}&order=start_date.desc`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function saveSemester(data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/semesters`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(data)
    }
  );
  return response.json();
}

async function deleteSemester(id) {
  await fetch(
    `${SUPABASE_URL}/rest/v1/semesters?id=eq.${id}`,
    {
      method: 'DELETE',
      headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY }
    }
  );
}

async function fetchHolidays(institutionId, instructorId) {
  if (!institutionId) return [];

  const headers = {
    'apikey': SUPABASE_KEY,
    'Authorization': 'Bearer ' + SUPABASE_KEY
  };

  const [generalResponse, personalResponse] = await Promise.all([
    fetch(
      `${SUPABASE_URL}/rest/v1/holidays?institution_id=eq.${institutionId}&is_personal=eq.false&select=*&order=date.asc`,
      { headers }
    ),
    fetch(
      `${SUPABASE_URL}/rest/v1/holidays?institution_id=eq.${institutionId}&is_personal=eq.true&instructor_id=eq.${instructorId}&select=*&order=date.asc`,
      { headers }
    )
  ]);

  if (!generalResponse.ok) {
    const detail = await generalResponse.text();
    throw new Error(
      `Failed to load institutional holidays (${generalResponse.status}): ${detail}`
    );
  }

  if (!personalResponse.ok) {
    const detail = await personalResponse.text();
    throw new Error(
      `Failed to load personal holidays (${personalResponse.status}): ${detail}`
    );
  }

  const [general, personal] = await Promise.all([
    generalResponse.json(),
    personalResponse.json()
  ]);

  return [
    ...(Array.isArray(general) ? general : []),
    ...(Array.isArray(personal) ? personal : [])
  ].sort((a, b) => a.date.localeCompare(b.date));
}

async function saveHoliday(data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/holidays`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(data)
    }
  );
  return response.json();
}

async function updateHoliday(
  id,
  data,
  institutionId
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/holidays?id=eq.${id}&institution_id=eq.${institutionId}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(data)
    }
  );
  return response.json();
}

async function deleteHoliday(
  id,
  institutionId
) {
  await fetch(
    `${SUPABASE_URL}/rest/v1/holidays?id=eq.${id}&institution_id=eq.${institutionId}`,
    {
      method: 'DELETE',
      headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY }
    }
  );
}

export default function SemesterSettings({ session }) {
 const [activeTab, setActiveTab] = useState('semesters');
  const [semesters, setSemesters] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Semester form
  const [season, setSeason] = useState('Spring');
  const [yearInput, setYearInput] = useState('');
  const [startDate, setStartDate] = useState('');

  // Holiday form
  const [holidayDate, setHolidayDate] = useState('');
  const [holidayReason, setHolidayReason] = useState('');
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [isPersonal, setIsPersonal] = useState(false);
const [selectedInstitutionId, setSelectedInstitutionId] = useState(
  session.isSuperadmin ? '' : session.institutionId
);
const [weekCount, setWeekCount] = useState(18);

  useEffect(() => {
  async function load() {
    if (session.isSuperadmin) {
      const inst = await fetchInstitutions();

if (Array.isArray(inst) && inst.length && !selectedInstitutionId) {
  setSelectedInstitutionId(inst[0].id);
}
    }
  }
  load();
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [session.isSuperadmin]);

useEffect(() => {
  if (!selectedInstitutionId) return;

  setLoading(true);
  Promise.all([
  fetchSemesters(selectedInstitutionId),
  fetchHolidays(
    selectedInstitutionId,
    session.instructorId
  )
])
    .then(([s, h]) => {
      setSemesters(Array.isArray(s) ? s : []);
      setHolidays(Array.isArray(h) ? h : []);
    })
    .finally(() => setLoading(false));
}, [selectedInstitutionId, session.instructorId]);

  async function handleAddSemester(e) {
    e.preventDefault();
    setError('');
    setSuccess('');

    const westernYear = parseYear(yearInput);
    if (!westernYear) {
      return setError(`Enter a valid year. Western: ${WESTERN_MIN}–${WESTERN_MAX}. Minguo: ${MINGUO_MIN}–${MINGUO_MAX}.`);
    }
    if (!startDate) return setError('Enter a start date.');
    const date = new Date(startDate);
    if (date.getDay() !== 1) return setError('Start date must be a Monday.');

    const name = `${season} ${westernYear}`;
    setSaving(true);
    try {
      const result = await saveSemester({
  name,
  start_date: startDate,
  institution_id: selectedInstitutionId,
  week_count: Number(weekCount) || 18
});
      setSemesters(prev => [result[0], ...prev]);
      setYearInput('');
      setStartDate('');
      setSuccess('Semester added.');
    } catch (err) {
      setError('Failed to save semester.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteSemester(id) {
    if (!window.confirm('Delete this semester? This cannot be undone.')) return;
    await deleteSemester(id);
    setSemesters(prev => prev.filter(s => s.id !== id));
    setSuccess('Semester deleted.');
  }

  async function handleAddHoliday(e) {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!holidayDate) return setError('Enter a date.');
    if (!holidayReason.trim()) return setError('Enter a reason.');
    const duplicate = holidays.find(h => h.date === holidayDate);
    if (duplicate) return setError('A holiday already exists on that date.');

    setSaving(true);
    try {
      const result = await saveHoliday({
  date: holidayDate,
  reason: holidayReason.trim(),
  is_personal: isPersonal,
  instructor_id: isPersonal
    ? session.instructorId
    : null,
  institution_id: isPersonal
    ? session.institutionId
    : selectedInstitutionId
});
      setHolidays(prev => [...prev, result[0]].sort((a, b) => a.date.localeCompare(b.date)));
      setHolidayDate('');
setHolidayReason('');
setIsPersonal(false);
setSuccess('Holiday added.');
    } catch (err) {
      setError('Failed to save holiday.');
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdateHoliday(e) {
    e.preventDefault();
    setError('');
    if (!editingHoliday.reason.trim()) return setError('Enter a reason.');
    setSaving(true);
    try {
      const result = await updateHoliday(
  editingHoliday.id,
  {
    date: editingHoliday.date,
    reason: editingHoliday.reason.trim()
  },
  editingHoliday.institution_id
);
      setHolidays(prev =>
        prev.map(h => h.id === editingHoliday.id ? result[0] : h)
          .sort((a, b) => a.date.localeCompare(b.date))
      );
      setEditingHoliday(null);
      setSuccess('Holiday updated.');
    } catch (err) {
      setError('Failed to update holiday.');
    } finally {
      setSaving(false);
    }
  }

  async function fetchInstitutions() {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/institutions?select=id,name&order=name.asc`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

  async function handleDeleteHoliday(holiday) {
  if (!window.confirm('Delete this holiday?')) return;

  await deleteHoliday(
    holiday.id,
    holiday.institution_id
  );

  setHolidays(prev =>
    prev.filter(h => h.id !== holiday.id)
  );

  setSuccess('Holiday deleted.');
}

  if (loading) return <div style={styles.muted}>Loading...</div>;

  return (
    <div>
      <div style={styles.tabs}>
        {[
  { key: 'semesters', label: 'Semesters' },
  { key: 'holidays', label: 'Holidays' },
].map(tab => (
          <button
            key={tab.key}
            style={{ ...styles.tab, ...(activeTab === tab.key ? styles.tabActive : {}) }}
            onClick={() => { setActiveTab(tab.key); setError(''); setSuccess(''); }}
          >
            {tab.label}
          </button>
        ))}
      </div>

{error && <div style={styles.error}>{error}</div>}
      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      {activeTab === 'semesters' && (
        <div>
          <h2 style={styles.subheading}>Semesters</h2>
          <p style={styles.muted}>
            The start date must always be a Monday. Year can be entered in Western ({WESTERN_MIN}–{WESTERN_MAX})
            or Minguo ({MINGUO_MIN}–{MINGUO_MAX}) format.
          </p>

          <form onSubmit={handleAddSemester} style={styles.form}>
            <div style={styles.row}>
              <div style={styles.field}>
                <label style={styles.label}>Season</label>
                <select style={styles.input} value={season} onChange={e => setSeason(e.target.value)}>
                  <option value="Spring">Spring</option>
                  <option value="Fall">Fall</option>
                </select>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Year</label>
                <input
                  style={styles.input}
                  type="text"
                  value={yearInput}
                  onChange={e => setYearInput(e.target.value)}
                  placeholder="e.g. 2026 or 115"
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Start date (Monday)</label>
                                <CourseDatePicker
                  value={
                    startDate
                  }
                  emptyLabel=
                    "Select semester start"
                  onChange={
                    setStartDate
                  }
                  holidays={
                    holidays
                  }
                />
              </div>
              <div style={styles.field}>
  <label style={styles.label}>Number of weeks</label>
  <input
    style={styles.input}
    type="number"
    min="1"
    max="52"
    value={weekCount}
    onChange={e => setWeekCount(e.target.value)}
    placeholder="18"
  />
</div>
              <div style={styles.fieldBtn}>
                <label style={styles.label}>&nbsp;</label>
                <button style={styles.btn} disabled={saving}>
                  {saving ? 'Adding...' : 'Add Semester'}
                </button>
              </div>
            </div>
          </form>

          {semesters.length === 0 && <div style={styles.muted}>No semesters added yet.</div>}
          {semesters.length > 0 && (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Start Date</th>
<th style={styles.th}>Weeks</th>
<th style={styles.th}></th>
                </tr>
              </thead>
              <tbody>
                {semesters.map(s => (
                  <tr key={s.id}>
                    <td style={styles.td}>{s.name}</td>
                    <td style={styles.td}>{s.start_date}</td>
<td style={styles.td}>{s.week_count || 18}</td>
<td style={styles.td}>
  <button style={styles.dangerBtn} onClick={() => handleDeleteSemester(s.id)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'holidays' && (
        <div>
          <h2 style={styles.subheading}>Holidays</h2>
          <p style={styles.muted}>
            Enter all days off for the year. Holidays that fall on a class day will
            automatically mark that week as "No Class" in the Teaching Schedule.
          </p>

          <form onSubmit={handleAddHoliday} style={styles.form}>
            <div style={styles.row}>
              <div style={styles.field}>
                <label style={styles.label}>Date</label>
                                <CourseDatePicker
                  value={
                    holidayDate
                  }
                  emptyLabel=
                    "Select holiday date"
                  onChange={
                    setHolidayDate
                  }
                  holidays={
                    holidays
                  }
                />
              </div>
              <div style={{ ...styles.field, flex: 2 }}>
                <label style={styles.label}>Reason</label>
                <input
                  style={styles.input}
                  type="text"
                  value={holidayReason}
                  onChange={e => setHolidayReason(e.target.value)}
                  placeholder="e.g. National Day, Dragon Boat Festival"
                />
              </div>
              {session.isSuperadmin && (
  <div style={styles.fieldBtn}>
    <label style={styles.label}>&nbsp;</label>
    <label style={styles.checkLabel}>
      <input
        type="checkbox"
        checked={isPersonal}
        onChange={e => setIsPersonal(e.target.checked)}
        style={{ marginRight: '8px' }}
      />
      Personal (only affects my courses)
    </label>
  </div>
)}
              <div style={styles.fieldBtn}>
                <label style={styles.label}>&nbsp;</label>
                <button style={styles.btn} disabled={saving}>
                  {saving ? 'Adding...' : 'Add Holiday'}
                </button>
              </div>
            </div>
          </form>

          {holidays.length === 0 && <div style={styles.muted}>No holidays entered yet.</div>}
          {holidays.length > 0 && (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Date</th>
<th style={styles.th}>Reason</th>
<th style={styles.th}>Type</th>
<th style={styles.th}></th>
                </tr>
              </thead>
              <tbody>
                {holidays.map(h => (
                  <tr key={h.id}>
                    <td style={styles.td}>
                      {editingHoliday?.id === h.id ? (
                                                <CourseDatePicker
                          value={
                            editingHoliday
                              .date ||
                            ''
                          }
                          onChange={
                            value =>
                              setEditingHoliday(
                                prev => ({
                                  ...prev,
                                  date:
                                    value
                                })
                              )
                          }
                          holidays={
                            holidays
                          }
                        />
                      ) : h.date}
                    </td>
                    <td style={styles.td}>
                      {editingHoliday?.id === h.id ? (
                        <form onSubmit={handleUpdateHoliday} style={{ display: 'flex', gap: '8px' }}>
                          <input
                            style={styles.input}
                            type="text"
                            value={editingHoliday.reason}
                            onChange={e => setEditingHoliday(prev => ({ ...prev, reason: e.target.value }))}
                            autoFocus
                          />
                          <button style={styles.btn} disabled={saving}>Save</button>
                          <button type="button" style={styles.secondaryBtn} onClick={() => setEditingHoliday(null)}>Cancel</button>
                        </form>
                      ) : h.reason}
                    </td>
                    <td style={styles.td}>{h.is_personal ? 'Personal' : 'Global'}</td>
                    <td style={styles.td}>
                      {editingHoliday?.id !== h.id && (
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button style={styles.editBtn} onClick={() => setEditingHoliday({ ...h })}>Edit</button>
                          <button style={styles.dangerBtn} onClick={() => handleDeleteHoliday(h)}>Delete</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  subheading: { margin: '0 0 8px', fontSize: '18px', color: '#1f2933' },
  muted: { color: '#66788a', fontSize: '14px', margin: '0 0 20px', lineHeight: '1.5' },
  error: {
    background: '#faeaea', border: '1px solid #e8bcbc', borderRadius: '8px',
    padding: '10px 14px', color: '#a12a2a', fontSize: '14px', marginBottom: '16px'
  },
  success: {
    background: '#e7f5ed', border: '1px solid #b9dfc9', borderRadius: '8px',
    padding: '10px 14px', color: '#216e45', fontSize: '14px', marginBottom: '16px'
  },
  tabs: {
    display: 'flex', gap: '4px', borderBottom: '2px solid #d7dee5', marginBottom: '20px'
  },
  tab: {
    border: 'none', background: 'transparent', padding: '8px 14px',
    fontSize: '14px', fontWeight: '600', color: '#66788a', cursor: 'pointer',
    borderBottom: '2px solid transparent', marginBottom: '-2px'
  },
  tabActive: { color: '#215b7d', borderBottom: '2px solid #215b7d' },
  form: { marginBottom: '24px' },
  row: { display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' },
  field: { flex: 1, minWidth: '140px' },
  fieldBtn: { flexShrink: 0 },
  label: { display: 'block', fontWeight: '700', fontSize: '13px', marginBottom: '6px', color: '#1f2933' },
  input: {
    width: '100%', border: '1px solid #d7dee5', borderRadius: '8px',
    padding: '9px 11px', fontSize: '14px', boxSizing: 'border-box', background: 'white'
  },
  btn: {
    background: '#215b7d', color: 'white', border: 'none', borderRadius: '8px',
    padding: '9px 16px', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
  },
  secondaryBtn: {
    background: 'transparent', color: '#66788a', border: '1px solid #d7dee5',
    borderRadius: '8px', padding: '9px 16px', fontSize: '14px', cursor: 'pointer'
  },
  editBtn: {
    background: 'transparent', color: '#215b7d', border: '1px solid #b0cfe0',
    borderRadius: '6px', padding: '5px 10px', fontSize: '13px', cursor: 'pointer'
  },
  dangerBtn: {
    background: 'transparent', color: '#a12a2a', border: '1px solid #e2b8b8',
    borderRadius: '6px', padding: '5px 10px', fontSize: '13px', cursor: 'pointer'
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left', padding: '9px 11px', fontSize: '13px',
    color: '#42515f', background: '#f0f4f7', borderBottom: '1px solid #d7dee5'
  },
  td: { padding: '10px 11px', borderBottom: '1px solid #d7dee5', fontSize: '14px' },
checkLabel: { fontSize: '14px', color: '#1f2933', display: 'flex', alignItems: 'center', cursor: 'pointer' }
};